import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, UserPlus, Users, Search, Check, UserCheck } from 'lucide-react';
import { useFriends, FriendProfile } from '../hooks/useFriends';
import { cn } from '../lib/utils';

interface FriendsModalProps {
  uid: string;
  onClose: () => void;
  onChallenge: (friendUid: string, friendName: string) => void;
}

type TabId = 'friends' | 'requests' | 'search';

const TABS: { id: TabId; label: string }[] = [
  { id: 'friends', label: '👥 Amici' },
  { id: 'requests', label: '📥 Richieste' },
  { id: 'search', label: '🔍 Cerca' },
];

function FriendRow({
  friend,
  onChallenge,
  onRemove,
}: {
  friend: FriendProfile;
  onChallenge: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-lg font-bold">
        {friend.avatar || '🎴'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-white text-sm truncate">
          {friend.displayName || 'Anonymous'}
        </div>
        <div className="text-xs text-white/40">
          W: {friend.stats?.won ?? 0}
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onChallenge}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-full transition-colors"
        >
          Sfida
        </button>
        <button
          onClick={onRemove}
          className="px-3 py-1.5 bg-white/10 hover:bg-red-500/50 text-white/60 hover:text-white text-xs font-bold rounded-full transition-colors"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function RequestRow({
  profile,
  onAccept,
  onDecline,
}: {
  profile: FriendProfile;
  onAccept: () => void;
  onDecline: () => void;
}) {
  return (
    <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-lg font-bold">
        {profile.avatar || '🎴'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-white text-sm truncate">
          {profile.displayName || 'Anonymous'}
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={onAccept}
          className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-bold rounded-full transition-colors"
        >
          Accetta
        </button>
        <button
          onClick={onDecline}
          className="px-3 py-1.5 bg-white/10 hover:bg-red-500/50 text-white/60 hover:text-white text-xs font-bold rounded-full transition-colors"
        >
          Rifiuta
        </button>
      </div>
    </div>
  );
}

function SearchResultRow({
  profile,
  status,
  onSendRequest,
}: {
  profile: FriendProfile;
  status: 'none' | 'outgoing' | 'already';
  onSendRequest: () => void;
}) {
  return (
    <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-lg font-bold">
        {profile.avatar || '🎴'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-white text-sm truncate">
          {profile.displayName || 'Anonymous'}
        </div>
      </div>
      {status === 'outgoing' ? (
        <span className="px-3 py-1.5 bg-green-600/30 text-green-400 text-xs font-bold rounded-full flex items-center gap-1">
          <Check className="w-3 h-3" /> Inviata
        </span>
      ) : status === 'already' ? (
        <span className="px-3 py-1.5 bg-white/10 text-white/40 text-xs font-bold rounded-full flex items-center gap-1">
          <UserCheck className="w-3 h-3" /> Amico
        </span>
      ) : (
        <button
          onClick={onSendRequest}
          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-full transition-colors flex items-center gap-1"
        >
          <UserPlus className="w-3 h-3" /> Invia richiesta
        </button>
      )}
    </div>
  );
}

export default function FriendsModal({ uid, onClose, onChallenge }: FriendsModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('friends');
  const {
    friends,
    incoming,
    outgoing,
    sendFriendRequest,
    acceptFriendRequest,
    declineFriendRequest,
    removeFriend,
    searchUsers,
  } = useFriends(uid);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FriendProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [notifying, setNotifying] = useState<string | null>(null);

  const showNotification = (msg: string) => {
    setNotifying(msg);
    setTimeout(() => setNotifying(null), 2000);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) return;
    setSearching(true);
    const results = await searchUsers(searchQuery);
    setSearchResults(results);
    setSearching(false);
  };

  const handleSendRequest = async (toUid: string) => {
    try {
      await sendFriendRequest(toUid);
      showNotification('Richiesta inviata! ✅');
    } catch {
      showNotification('Errore: impossibile inviare la richiesta');
      return;
    }
    // Refresh results to show "Inviata" state
    setSearchResults((prev) =>
      prev.map((r) => (r.uid === toUid ? { ...r } : r))
    );
  };

  const handleRemove = async (friendUid: string) => {
    await removeFriend(friendUid);
    showNotification('Amico rimosso');
  };

  const handleAccept = async (fromUid: string) => {
    await acceptFriendRequest(fromUid);
    showNotification('Amico aggiunto! 🎉');
  };

  const handleDecline = async (fromUid: string) => {
    await declineFriendRequest(fromUid);
  };

  const getResultStatus = (resultUid: string): 'none' | 'outgoing' | 'already' => {
    if (friends.some((f) => f.uid === resultUid)) return 'already';
    if (outgoing.includes(resultUid)) return 'outgoing';
    return 'none';
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1e] rounded-2xl w-full max-w-md max-h-[85vh] overflow-hidden flex flex-col"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-yellow-400" />
            <h2 className="text-lg font-bold text-white">Amici</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex-1 py-2.5 text-xs font-bold transition-colors border-b-2',
                activeTab === tab.id
                  ? 'border-yellow-400 text-yellow-400'
                  : 'border-transparent text-white/40 hover:text-white/70'
              )}
            >
              {tab.label}
              {tab.id === 'requests' && incoming.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">
                  {incoming.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.15 }}
            >
              {/* Amici tab */}
              {activeTab === 'friends' && (
                <div className="space-y-3">
                  {friends.length === 0 ? (
                    <div className="text-center py-8 text-white/40">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Nessun amico ancora.</p>
                      <p className="text-sm mt-1">Cerca qualcuno!</p>
                    </div>
                  ) : (
                    friends.map((friend) => (
                      <div key={friend.uid}>
                        <FriendRow
                          friend={friend}
                          onChallenge={() => onChallenge(friend.uid, friend.displayName || 'Anonymous')}
                          onRemove={() => handleRemove(friend.uid)}
                        />
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Richieste tab */}
              {activeTab === 'requests' && (
                <div className="space-y-3">
                  {incoming.length === 0 ? (
                    <div className="text-center py-8 text-white/40">
                      <UserPlus className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Nessuna richiesta</p>
                    </div>
                  ) : (
                    incoming.map((req) => (
                      <div key={req.uid}>
                        <RequestRow
                          profile={req}
                          onAccept={() => handleAccept(req.uid)}
                          onDecline={() => handleDecline(req.uid)}
                        />
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Cerca tab */}
              {activeTab === 'search' && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        placeholder="Cerca per nome..."
                        className="w-full pl-9 pr-4 py-2.5 bg-white/10 border border-white/10 rounded-xl text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-yellow-400/50"
                      />
                    </div>
                    <button
                      onClick={handleSearch}
                      disabled={searching || searchQuery.length < 2}
                      className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-white/10 disabled:text-white/30 text-white text-sm font-bold rounded-xl transition-colors"
                    >
                      {searching ? '...' : 'Cerca'}
                    </button>
                  </div>

                  {searchResults.length === 0 && !searching && searchQuery.length >= 2 && (
                    <div className="text-center py-6 text-white/40">
                      <Search className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Nessun risultato</p>
                    </div>
                  )}

                  {searchResults.length > 0 && (
                    <div className="space-y-2">
                      {searchResults.map((result) => (
                        <div key={result.uid}>
                          <SearchResultRow
                            profile={result}
                            status={getResultStatus(result.uid)}
                            onSendRequest={() => handleSendRequest(result.uid)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Notification toast */}
      <AnimatePresence>
        {notifying && (
          <motion.div
            className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white text-black px-4 py-2 rounded-full font-bold text-sm"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            {notifying}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}